import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@scio3/ui';
import { DELETE_COMPANY } from '@scio3/graphql-client';
import { signOut, auth } from '@/lib/firebase';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/usePermissions';
import { useDialogSubmitState } from '@/hooks/useDialogSubmitState';
import { useBillingDialogStore } from '@/features/billing/stores/billing-dialog-store';
import { useBillingData } from '@/features/billing/hooks/useBillingData';
import { SubscriptionWizardDialog } from '@/features/billing/dialogs/SubscriptionWizardDialog';
import { BillingInformationDialog } from '@/features/billing/dialogs/BillingInformationDialog';
import { DowngradeDialog } from '@/features/billing/dialogs/DowngradeDialog';
import { DowngradeComparisonDialog } from '@/features/billing/dialogs/DowngradeComparisonDialog';
import { DowngradeSurveyDialog } from '@/features/billing/dialogs/DowngradeSurveyDialog';
import { UnpaidInvoicesDialog } from '@/features/billing/dialogs/UnpaidInvoicesDialog';
import { ScreenManagementDialogFromStore } from '@/features/billing/dialogs/BillingDialogs';

interface PaymentData {
  isTrial?: boolean;
  isActive?: boolean;
}

export function isOptiSignsDomain(): boolean {
  const host = window.location.hostname;
  return (
    host === 'localhost' ||
    host === 'optisigns.com' ||
    host.endsWith('.optisigns.com') ||
    host.endsWith('.opstech.org')
  );
}

// ---------------------------------------------------------------------------
// DeleteAccountDialog — separate confirmation dialog matching scio1 UX
// ---------------------------------------------------------------------------

function DeleteAccountDialog({
  open,
  onOpenChange,
  onConfirm,
  deleting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  deleting: boolean;
}) {
  const { t } = useTranslation();
  const [confirmText, setConfirmText] = useState('');
  const isConfirmed = confirmText === 'DELETE';
  const { disabled, disabledTooltip } = useDialogSubmitState({
    isSubmitting: deleting,
    isConfirmed,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle>{t('Delete Account')}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-4">
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                {t('deleteAccountModal1_prefix')}{' '}
                <span className="font-semibold text-destructive">
                  {t('This action cannot be undone.')}
                </span>{' '}
                {t('Do you wish to continue?')}
              </div>
              <div className="space-y-2">
                <Input
                  data-testid="delete-account-confirm-input"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                />
                <p className="text-sm text-muted-foreground">
                  {t('Please type "DELETE" and click Delete Account to continue.')}
                </p>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{t('Cancel')}</Button>
          </DialogClose>
          <Button
            variant="destructive"
            data-testid="delete-account-ok-btn"
            onClick={onConfirm}
            disabled={disabled}
            disabledTooltip={disabledTooltip}
          >
            {t('OK')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// InactiveAccountDialog — main blocking dialog
// ---------------------------------------------------------------------------

export function InactiveAccountDialog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.me);
  const { isRootOwner } = usePermissions();
  const [deleteCompany] = useMutation(DELETE_COMPANY);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const openBillingDialog = useBillingDialogStore((s) => s.openDialog);
  const activeBillingDialog = useBillingDialogStore((s) => s.activeDialog);
  // Reads from the auth store (instant, no network) — gives the account's
  // remaining screen quota, used to tell "cancelled" apart from "card declined".
  const { maxScreens } = useBillingData();

  // Hide the Attention alert while a child dialog (Delete Account confirm or any billing dialog)
  // is open so it doesn't visually stack on top. When the child closes and the account is still
  // inactive, AppShell keeps rendering us → the alert reappears.
  const childDialogOpen = showDeleteDialog || activeBillingDialog !== null;

  const payment = me?.payment as PaymentData | undefined;
  const isTrial = payment?.isTrial === true;
  const isWhiteLabel = !!me?.brandingData?.isWhiteLabel;
  const showUpdate = isOptiSignsDomain() && !isWhiteLabel;
  const showDelete = isRootOwner && !isWhiteLabel;

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch {
      // Even if signOut fails, navigate away — auth state clears on next load
    }
    navigate('/signIn');
  };

  // Open the native scio3 billing flow. Recovery: the wizard/BillingInformationDialog
  // refetches ME_QUERY on success → auth-store `isAccountInactive` flips false →
  // AppShell unmounts this paywall gate automatically.
  //
  // #1413 — Route to the right flow based on WHY the account is inactive:
  //  - Trial expired OR subscription cancelled (no remaining screen quota,
  //    maxScreens === 0): the user has no active subscription, so updating only
  //    the card would leave them at 0/0 screens. Open the subscription wizard,
  //    which starts at the "Number of Screens" step (defaulting to 1) so they can
  //    actually re-subscribe.
  //  - Payment failed but a subscription/quota still exists (maxScreens > 0):
  //    they only need a fresh card → keep the card-only Billing Information dialog.
  const needsResubscribe = isTrial || maxScreens === 0;
  const handleUpdate = () => {
    openBillingDialog(needsResubscribe ? 'subscriptionWizard' : 'billingInformation');
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteCompany();
      await signOut(auth);
      navigate('/signIn');
    } catch {
      toast.error(t('Failed to delete account. Please try again or contact support.'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <AlertDialog open={!childDialogOpen}>
        <AlertDialogContent
          data-testid="inactive-account-dialog"
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{t('Attention')}</AlertDialogTitle>
            <AlertDialogDescription>
              {isTrial
                ? t('freePlanDowngradeInactive')
                : t(
                    'Your subscription needs attention. Please update your payment information to continue.',
                  )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:flex-row sm:justify-between">
            {showDelete && (
              <Button
                variant="destructive"
                data-testid="inactive-delete-account-btn"
                onClick={() => setShowDeleteDialog(true)}
              >
                {t('Delete Account')}
              </Button>
            )}
            <div className="flex gap-2 sm:ml-auto">
              <Button variant="outline" data-testid="inactive-logout-btn" onClick={handleLogout}>
                {t('Logout')}
              </Button>
              {showUpdate && (
                <Button data-testid="inactive-update-btn" onClick={handleUpdate}>
                  {isTrial ? t('Update Subscription') : t('Update')}
                </Button>
              )}
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showDelete && (
        <DeleteAccountDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}

      {/* Mount native billing dialogs here so they're available during the paywall lockout —
          AppShell's isAccountInactive branch returns early, before <BillingDialogs /> mounts.
          SubscriptionWizardDialog → Free selection opens DowngradeDialog and can surface
          UnpaidInvoicesDialog as a pre-check, so both must be mounted alongside.
          ScreenManagementDialogFromStore is also needed because the wizard's trial→Free
          5555 path calls openScreenManagement(...) on the global store — without this
          mount the store update has no listener and the dialog never appears. */}
      <SubscriptionWizardDialog />
      <BillingInformationDialog />
      <DowngradeSurveyDialog />
      <DowngradeComparisonDialog />
      <DowngradeDialog />
      <UnpaidInvoicesDialog />
      <ScreenManagementDialogFromStore />
    </>
  );
}
